//@flow
import type { ThunkAction, ID } from '../../core/types'
import type {
  Checkout,
  CheckoutItem,
  ShippingOption,
  DeliveryType,
  HomeDeliveryMode
} from './types'
import { normalize } from 'normalizr'
import { services } from '../../core/feathers'
import { schemas, domains } from '../constants'
import {
  isCommInProgress,
  startCommunication,
  endCommunication
} from '../../modules/communication'
import { getUserId } from '../session'
import {
  FETCH_USER_CHECKOUTS_REQUEST,
  FETCH_USER_CHECKOUTS_SUCCESS,
  FETCH_USER_CHECKOUTS_FAILED,
  FETCH_PENDING_CHECKOUTS_REQUEST,
  FETCH_PENDING_CHECKOUTS_SUCCESS,
  FETCH_PENDING_CHECKOUTS_FAILED,
  STORE_CHECKOUTS,
  STORE_CHECKOUT_ITEMS,
  CHECKOUT_STATE,
  USER_CHECKOUTS_SET,
  USER_CHECKOUT_ITEMS_SET,
  SAVE_CHECKOUT_REQUEST,
  SAVE_CHECKOUT_SUCCESS,
  SAVE_CHECKOUT_FAILED,
  CLEAN_CHECKOUTS,
  CLEAN_CHECKOUT_ITEMS,
  FETCH_USER_CHECKOUTS_RESOURCE,
  FETCH_PENDING_CHECKOUTS_RESOURCE,
  FETCH_CHECKOUT_WITH_AUCTION_RESOURCE,
  FETCH_CHECKOUT_WITH_AUCTION_REQUEST,
  FETCH_CHECKOUT_WITH_AUCTION_FAILED,
  STORE_CHECKOUT_WITH_AUCTION,
  FETCH_CHECKOUT_WITH_AUCTION_SUCCESS,
  createCheckoutSaveResource
} from './constants'
import { mergeEntities, update, removeAll } from '../../modules/entities'
import { sadd, sdel } from '../../modules/set'
import { batch } from '../../modules/lib/batchReducer'
import { hset, type SetAction } from '../../modules/hash'

export function normalizeCheckoutData(checkouts: Checkout[] | Checkout) {
  return normalize(
    checkouts,
    Array.isArray(checkouts) ? [schemas.checkout] : schemas.checkout
  )
}

export function storeCheckouts(
  checkouts: Checkout[],
  actionType: string = STORE_CHECKOUTS
) {
  const { entities, result } = normalizeCheckoutData(checkouts)
  return batch(
    actionType,
    mergeEntities(entities),
    sadd(USER_CHECKOUTS_SET, result)
  )
}

export function cleanCheckouts() {
  return batch(
    CLEAN_CHECKOUTS,
    sdel(USER_CHECKOUTS_SET),
    removeAll(domains.CHECKOUT),
    cleanCheckoutItems()
  )
}

export function normalizeCheckoutItemData(
  checkoutItems: CheckoutItem[] | CheckoutItem
) {
  return normalize(
    checkoutItems,
    Array.isArray(checkoutItems) ? [schemas.checkoutItem] : schemas.checkoutItem
  )
}

export function storeCheckoutItems(
  checkoutItems: CheckoutItem[],
  actionType: string = STORE_CHECKOUT_ITEMS
) {
  const { entities, result } = normalizeCheckoutItemData(checkoutItems)
  return batch(
    actionType,
    mergeEntities(entities),
    sadd(USER_CHECKOUT_ITEMS_SET, result)
  )
}

export function cleanCheckoutItems() {
  return batch(
    CLEAN_CHECKOUT_ITEMS,
    sdel(USER_CHECKOUT_ITEMS_SET),
    removeAll(domains.CHECKOUT_ITEM)
  )
}

export function fetchCheckouts(): ThunkAction {
  return async function(dispatch, getState) {
    const resource = FETCH_USER_CHECKOUTS_RESOURCE
    const userId = getUserId(getState())
    if (!userId) return
    if (isCommInProgress(resource, getState())) {
      return
    }
    try {
      dispatch(
        batch(FETCH_USER_CHECKOUTS_REQUEST, startCommunication(resource))
      )
      const data: Checkout[] = await services.checkout.find({
        query: {
          userId,
          $eager: '[auction, checkoutItems.[item]]'
        }
      })
      if (data.length) {
        dispatch(storeCheckouts(data))
      }
      dispatch(batch(FETCH_USER_CHECKOUTS_SUCCESS, endCommunication(resource)))

      return true
    } catch (error) {
      console.warn('Error fetching checkouts', error)
      dispatch(
        batch(FETCH_USER_CHECKOUTS_FAILED, endCommunication(resource, false))
      )
      return false
    }
  }
}

export function fetchPendingCheckouts(): ThunkAction {
  return async function(dispatch, getState) {
    const resource = FETCH_PENDING_CHECKOUTS_RESOURCE
    const userId = getUserId(getState())
    if (!userId) return
    if (isCommInProgress(resource, getState())) {
      return
    }
    try {
      dispatch(
        batch(FETCH_PENDING_CHECKOUTS_REQUEST, startCommunication(resource))
      )
      const data: Checkout[] = await services.checkout.find({
        query: {
          userId,
          state: {
            $in: [CHECKOUT_STATE.PAYMENT_OK, CHECKOUT_STATE.PAYMENT_FAIL]
          },
          $eager: '[auction, checkoutItems.[item]]'
        }
      })
      if (data.length) {
        dispatch(storeCheckouts(data))
      }
      dispatch(
        batch(FETCH_PENDING_CHECKOUTS_SUCCESS, endCommunication(resource))
      )
      return true
    } catch (error) {
      console.warn('Error fetching pending checkouts', error)
      dispatch(
        batch(FETCH_PENDING_CHECKOUTS_FAILED, endCommunication(resource, false))
      )
      return false
    }
  }
}

// update shipping details from UI before saving to backend
export function updateCheckout(checkoutId: string, data: {}) {
  return update(domains.CHECKOUT, checkoutId, data)
}

export function saveCheckout(checkoutId: string, data: {}): ThunkAction {
  return async function(dispatch, getState) {
    if (!checkoutId) {
      console.error('saveCheckout without id?!?')
      return false
    }
    const resource = createCheckoutSaveResource(checkoutId)
    if (isCommInProgress(resource, getState())) {
      return true
    }
    dispatch(batch(SAVE_CHECKOUT_REQUEST, startCommunication(resource)))
    try {
      const patchData = {
        ...data,
        state: data.shippingOption ? CHECKOUT_STATE.COMPLETED : undefined
      }
      const savedCheckout = await services.checkout.patch(checkoutId, patchData)
      dispatch(batch(endCommunication(resource)))
      dispatch(storeCheckouts(savedCheckout, SAVE_CHECKOUT_SUCCESS))
      return savedCheckout
    } catch (error) {
      console.warn('Error saving checkout!')
      dispatch(batch(SAVE_CHECKOUT_FAILED, endCommunication(resource)))
    }
  }
}

export function fetchCheckoutWithAuction(checkoutId: ID): ThunkAction {
  return async (dispatch, getState) => {
    const resource = FETCH_CHECKOUT_WITH_AUCTION_RESOURCE
    if (isCommInProgress(resource, getState())) {
      console.log('fetchCheckoutWithAuction called but another one in progress')
      return false
    }
    dispatch(
      batch(FETCH_CHECKOUT_WITH_AUCTION_REQUEST, startCommunication(resource))
    )
    try {
      const checkoutItemWithAuction = await services[domains.CHECKOUT].get(
        checkoutId,
        { query: { $eager: '[auction]' } }
      )
      dispatch(
        batch(
          STORE_CHECKOUT_WITH_AUCTION,
          storeEntities(checkoutItemWithAuction, schemas.checkout)
        )
      )
      dispatch(
        batch(FETCH_CHECKOUT_WITH_AUCTION_SUCCESS, endCommunication(resource))
      )
    } catch (error) {
      console.warn('error fetching checkout', checkoutId)
      console.log(error)
      dispatch(
        batch(
          FETCH_CHECKOUT_WITH_AUCTION_FAILED,
          endCommunication(resource, false)
        )
      )
    }
  }
}

const storeEntities = (data: Object, schema: Object) => {
  const { entities } = normalize(data, Array.isArray(data) ? [schema] : schema)
  return mergeEntities(entities)
}

export const setDeliveryType = (type: DeliveryType): SetAction =>
  hset(domains.CHECKOUT, 'deliveryType', type)

export const setShippingOption = (option: ShippingOption): SetAction =>
  hset(domains.CHECKOUT, 'shippingOption', option)

export const setHomeDeliveryMode = (mode: HomeDeliveryMode): SetAction =>
  hset(domains.CHECKOUT, 'homeDeliveryMode', mode)

export const setUserAddressId = (addressId: string): SetAction =>
  hset(domains.CHECKOUT, 'userAddressId', addressId)



// WEBPACK FOOTER //
// ./src/services/checkout/actions.js